import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { WALL_DEFS, wallIdOfRegion } from "@gooner/shared";
import { api } from "../../lib/api";
import { useSession } from "../../lib/session";
import { useUntilFulfilled } from "../hooks/useUntilFulfilled";
import { DiscordLink } from "../../components/DiscordLink";
import { WallGlyph } from "../../components/WallGlyph";
import { Masked } from "../../lib/replayMask";

/** The same clock Gold runs, and now literally the same one — see useUntilFulfilled. */
const GIVE_UP_MS = 45_000;
const EVERY_MS = 2_000;

/**
 * Where PayzMoon sends a square's buyer back to.
 *
 * Same race as GoldThanks — the redirect usually beats the webhook — but the wall has
 * no per-user entitlement field to poll, so the observable effect of fulfillment is the
 * BOARD: this asks /wall/state until the `?region=` square's holder is the signed-in
 * user. That is also the honest signal, because it is exactly what everyone else sees.
 *
 * TIMING OUT IS NOT FAILING (GoldThanks' rule): a webhook can land minutes later, and
 * "your square is on its way" beats a lie in either direction. And a buyer who was
 * double-sold — displaced before ever seeing this page — still gets the on-its-way
 * message rather than a false failure; the board itself is the record.
 */
export function WallThanks() {
  const user = useSession((s) => s.user);
  const [params] = useSearchParams();
  const regionId = params.get("region") ?? "";
  // Which wall the square is on, read off its id — the landing page names the wall it
  // polls and links, and a Premium Wall square must not be looked for on the other board.
  const wall = WALL_DEFS[wallIdOfRegion(regionId) ?? "main"];
  // A free claim lands here too — same page, no payment leg, so the interim copy must
  // not talk about a payment that never happened. The placement was inserted before the
  // redirect, so the first poll normally already answers.
  const free = params.get("free") === "1";
  // A Faps purchase is the same shape as a free claim from here: no payment leg, the
  // placement inserted before the redirect. Only the interim sentence differs.
  const faps = params.get("faps") === "1";
  const [mine, setMine] = useState(false);
  // The wall has no per-user entitlement to read, so fulfillment is the BOARD saying the
  // square is theirs — the same answer everybody else is looking at.
  const { gaveUp } = useUntilFulfilled({
    enabled: !!user && !!regionId && !mine,
    everyMs: EVERY_MS,
    giveUpMs: GIVE_UP_MS,
    check: async (live) => {
      const { squares } = await api.wallState(wall.id);
      const sq = squares.find((s) => s.regionId === regionId);
      if (live() && user && sq?.holder?.username === user.username) {
        setMine(true);
        return true;
      }
    },
  });

  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16 text-center">
      {mine ? (
        <>
          <WallGlyph wallId={wall.id} size="lg" />
          <h1 className="mt-4 text-xl font-semibold text-zinc-100">Your square is live.</h1>
          <p className="mt-2 text-sm text-zinc-400">
            Your clips are on the {wall.name} under <Masked>@{user?.username}</Masked>, on every screen
            showing it, until somebody outbids you.
          </p>
        </>
      ) : gaveUp ? (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">
            {free || faps ? "Your claim is in." : "Payment received."}
          </h1>
          <p className="mt-2 text-sm text-zinc-400">
            Your square is taking a moment to land — this happens, and it sorts itself
            out. Check the wall in a few minutes; nothing needs doing.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-xl font-semibold text-zinc-100">Claiming your square…</h1>
          <p className="mt-2 text-sm text-zinc-400">
            {faps
              ? "Putting your clips up — paid from your Faps. This takes a moment."
              : free
                ? "Putting your clips up. This takes a moment."
                : "Waiting on the payment to confirm. This usually takes a few seconds."}
          </p>
        </>
      )}
      {/* The Community Wall is what a bare /screen plays; any other wall names itself. */}
      <Link
        to={wall.id === "main" ? "/screen" : `/screen?view=${encodeURIComponent(wall.viewId)}&code=0`}
        className="mt-6 rounded-lg bg-white/10 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-white/20"
      >
        See the wall
      </Link>
      {/* Only once the square is actually up. On the two waiting states the page is
          still resolving somebody's payment, and inviting them elsewhere mid-transaction
          reads as a brush-off — worse, it invites them to leave before they have seen
          the thing they just bought. */}
      {mine ? (
        <div className="mt-10 w-full border-t border-white/10 pt-6">
          <p className="text-sm text-zinc-400">Come tell people it's yours.</p>
          <DiscordLink className="mt-3" />
        </div>
      ) : null}
    </div>
  );
}
